import React from 'react';

// Actions
import {
  attachOmniRiskProfile,
  attachPortfolioRiskProfile,
  attachRiskProfile,
  removeItem,
  removePortfolioRiskProfile,
  removeRiskProfile,
  selectComparisonItem,
  SET_IS_ATTACHING_PORTFOLIO_RISK_PROFILE,
  SET_IS_ATTACHING_RISK_PROFILE,
  unselectComparisonItem,
} from '@/redux/modules/comparison';
import {
  attachProfileToPortfolio,
  calculatePortfolio,
  loadPortfolioForEdit,
  resetPortfolio,
  SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO,
} from '@/redux/modules/edit-portfolio';
import {
  getKycQuestionnaires,
  loadKycQuestionnaire,
  resetRiskProfile,
  SET_IS_RESETTING_RISK_PROFILE,
  setRecommendedRiskProfile,
} from '@/redux/modules/kyc-questionnaire';
import { getPortfolioById } from '@/redux/modules/portfolios';
import { selectIsLoadingSome } from '@/redux/modules/ui/loaders';

// Components
import RiskProfileCompare from './RiskProfileCompare';
import SingleItemView from './SingleItemView';
import { Button } from '@/views/components/buttons';
import { CreateKycQuestionnaire, SelectContactRiskProfileModal } from '@/views/components/modals';
import { EditPortfolio } from '@/views/components/edit-portfolio';
import { LoadingSpinner } from '@/views/components/common';

// Enums
import { OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Libraries
import classNames from 'classnames';
import { connect } from 'react-redux';

// Types
import CancellablePromise from '@/types/cancellable-promise';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import RiskProfileItem, { RISK_SCORE_TYPE, SelectedRiskProfile } from '@/types/risk-profile-item';
import ThemeMode from '@/types/theme-mode';
import User from '@/types/user';
import { CurrentContact } from '@/redux/modules/types/contacts';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';
import { RiskProfileRule, RiskProfileRuleType } from '@/types/risk-profiles';
import { StateOmniRiskProfileConfig } from '@/types/risk-profiles/omni/omni-risk-profile-context-types';

// Utils
import notification from '@/utils/notification';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { getActiveQuestionnaire, getOriginalRiskProfile, shouldSplitCashAndFixedIncome } from '@/utils/risk-profiles';
import { createCancellablePromise } from '@/utils/promise';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { portfolioIsNew } from '@/utils/portfolios';
import { addUpRiskToProfile } from '@/utils/up-risk';
import { ApiRequestConfig } from '@/utils/api';

type DispatchProps = {
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
  loadKycQuestionnaire: (questionnaire: KycQuestionnaire) => void;
  attachOmniRiskProfile: (profile: FormattedFullOmniRiskProfile) => void;
  attachRiskProfile: (profile: RiskProfileItem) => void;
  removeRiskProfile: () => void;
  selectComparisonItem: (item: ComparisonItem, unselectOthers?: boolean) => void;
  unselectComparisonItem: (item: ComparisonItem) => void;
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
    options?: ApiRequestConfig,
  ) => Promise<Portfolio>;
  loadPortfolioForEdit: (portfolio: Portfolio, comparisonItem?: ComparisonItem, isPinned?: boolean) => Promise<void>;
  resetPortfolio: () => void;
  removeItem: (id: string) => void;
  resetRiskProfile: () => void;
  attachPortfolioRiskProfile: (profile: RiskProfileItem) => void;
  removePortfolioRiskProfile: () => void;
  attachProfileToPortfolio: (profile: Partial<PortfolioRiskProfile>) => void;
  calculatePortfolio: (originalItem?: ComparisonItem, reallocate?: boolean) => Promise<void>;
  setRecommendedRiskProfile: (riskProfile: SelectedRiskProfile) => void;
};

type StateProps = {
  user?: User | null;
  locale: Locale;
  questionnaires: KycQuestionnaire[];
  currentQuestionnaire?: KycQuestionnaire;
  currentContact?: CurrentContact;
  selectedComparisonItems: ComparisonItem[];
  comparison: Comparison;
  editedPortfolio: Portfolio;
  editedComparisonItem?: ComparisonItem;
  readOnly?: boolean;
  newRiskProfile?: Partial<ContactRiskProfile>;
  recommendedRiskProfile?: RiskProfileItem;
  isSavingRiskProfile?: boolean;
  theme?: ThemeMode;
};

type Props = DispatchProps &
  StateProps & {
    riskProfile?: RiskProfileItem | StateOmniRiskProfileConfig;
    onSavePortfolio: (portfolio: Portfolio, contact?: GenericContact, reset?: boolean) => Promise<Portfolio>;
    dark?: boolean;
  };

type State = {
  showRiskProfileModal: boolean;
  showSelectRiskProfileModal: boolean;
  riskProfileModalType?: RISK_PROFILE_MODAL_TYPE;
};

enum RISK_PROFILE_MODAL_TYPE {
  COMPARISON = 'comparison',
  PORTFOLIO = 'portfolio',
}

class RiskProfileView extends React.Component<Props, State> {
  readonly state: State = {
    showRiskProfileModal: false,
    showSelectRiskProfileModal: false,
    riskProfileModalType: undefined,
  };

  private portfolioPromise: Nullable<CancellablePromise<Portfolio>> = null;

  componentDidMount() {
    const { selectedComparisonItems, comparison } = this.props;

    this.props.getKycQuestionnaires();

    for (const item of selectedComparisonItems.slice(1)) {
      this.props.unselectComparisonItem(item);
    }

    // Select first item if none are selected
    if (selectedComparisonItems.length === 0 && comparison.items.length > 0) {
      this.props.selectComparisonItem(comparison.items[0]);
    }

    this.loadPortfolioData();
  }

  componentDidUpdate(prevProps: Props) {
    const { editedComparisonItem: prevEditedComparisonItem } = prevProps;
    const { editedComparisonItem, editedPortfolio, riskProfile } = this.props;

    const selectedItem = this.getSelectedItem();
    const prevSelectedItem: ComparisonItem | undefined = prevProps.selectedComparisonItems[0];
    const prevEditedId = prevEditedComparisonItem && prevEditedComparisonItem._id;

    if (editedComparisonItem && prevEditedId !== editedComparisonItem._id) {
      this.props.selectComparisonItem(editedComparisonItem, true);

      if (riskProfile === undefined && editedComparisonItem.riskProfile !== undefined) {
        this.props.attachPortfolioRiskProfile(editedComparisonItem.riskProfile);
      }
      return;
    }

    // Check if selection has changed
    const selectionHasChanged = !prevSelectedItem || !selectedItem || selectedItem._id !== prevSelectedItem._id;

    if (selectionHasChanged) {
      const noSelection = !selectedItem && editedPortfolio._id;
      const itemChanged = selectedItem && editedPortfolio._id && selectedItem._id !== editedPortfolio._id;

      if (noSelection || itemChanged) {
        this.props.resetPortfolio();
        this.props.removePortfolioRiskProfile();
      }

      if (prevSelectedItem && prevSelectedItem._id.startsWith('new-')) {
        this.props.removeItem(prevSelectedItem._id);
        this.props.removePortfolioRiskProfile();
      }

      if (this.portfolioPromise) {
        this.portfolioPromise.cancel();
      }

      this.loadPortfolioData();
    }
  }

  componentWillUnmount() {
    this.props.resetPortfolio();
  }

  loadPortfolioData = () => {
    const selectedItem = this.getSelectedItem();

    if (!selectedItem || !selectedItem._id || selectedItem._id.startsWith('new-') || selectedItem.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return;
    }

    this.portfolioPromise = createCancellablePromise(
      this.props.getPortfolioById(selectedItem._id, selectedItem.portfolioType, selectedItem.contact, selectedItem.enterprise, undefined, selectedItem.source),
    );
    this.portfolioPromise.promise.then(portfolio => {
      this.props.loadPortfolioForEdit(portfolio, selectedItem);
    });
  };

  getSelectedItem = (): ComparisonItem | undefined => {
    const { selectedComparisonItems, editedComparisonItem } = this.props;

    if (selectedComparisonItems.length === 0) {
      return undefined;
    }

    const item = selectedComparisonItems[0];

    if (editedComparisonItem && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && item._id === editedComparisonItem._id) {
      return editedComparisonItem;
    }

    return item;
  };

  hideRiskProfileModal = () => {
    this.props.resetRiskProfile();
    this.setState({ showRiskProfileModal: false, riskProfileModalType: undefined });
  };

  showRiskProfileModal = (type: RISK_PROFILE_MODAL_TYPE) => {
    const { questionnaires, user, comparison, editedPortfolio, loadKycQuestionnaire, setRecommendedRiskProfile } = this.props;

    if (!user) {
      return;
    }

    const questionnaire = getActiveQuestionnaire(questionnaires);
    loadKycQuestionnaire(questionnaire);
    this.setState({ showRiskProfileModal: true, riskProfileModalType: type });

    if (userHasPermissionEnabled(user, PERMISSIONS.KYC_UP_RISK_WARNING)) {
      switch (type) {
        case RISK_PROFILE_MODAL_TYPE.COMPARISON:
          if (comparison.riskProfile && 'upRisk' in comparison.riskProfile) {
            setRecommendedRiskProfile(comparison.riskProfile);
          }
          break;
        case RISK_PROFILE_MODAL_TYPE.PORTFOLIO:
          const editedRiskProfile = editedPortfolio.riskProfile?.profile;
          if (editedRiskProfile) {
            setRecommendedRiskProfile(editedRiskProfile);
          }
          break;
      }
    }
  };

  attachProfileToComparison = (riskProfile: Partial<ContactRiskProfile | FormattedFullOmniRiskProfile>) => {
    const { attachOmniRiskProfile, attachRiskProfile, resetRiskProfile, user } = this.props;
    const isOmniRiskProfile =
      userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE) && riskProfile && 'formId' in riskProfile && 'omniFormUrl' in riskProfile;

    if (isOmniRiskProfile) {
      if (!riskProfile || riskProfile.status !== OmniRiskProfileStatus.Approved) return notification.errorFromKey('DEFAULT_ERROR');
      attachOmniRiskProfile(riskProfile as FormattedFullOmniRiskProfile);
    } else {
      if (!riskProfile.profile) return notification.errorFromKey('DEFAULT_ERROR');
      const { profile, savedSections, questionnaire, externalData } = riskProfile as ContactRiskProfile;
      attachRiskProfile({
        ...profile,
        savedSections,
        questionnaireId: questionnaire,
        externalData,
      });
    }

    resetRiskProfile();
  };

  attachProfileToPortfolio = (profile: Partial<PortfolioRiskProfile>) => {
    const { comparison } = this.props;

    if (!profile.profile) {
      notification.errorFromKey('DEFAULT_ERROR');
      return;
    }

    this.props.attachProfileToPortfolio(profile);
    if (comparison.riskProfile === undefined) {
      this.props.attachPortfolioRiskProfile(profile.profile);
    }
    this.props.resetRiskProfile();
  };

  onDeletePortfolioRiskProfile = () => {
    this.props.removePortfolioRiskProfile();
  };

  showSelectRiskProfileModal = () => {
    this.setState({ showSelectRiskProfileModal: true });
  };

  hideSelectRiskProfileModal = () => {
    this.setState({ showSelectRiskProfileModal: false });
  };

  onCancelCreatePortfolio = () => {
    const { comparison } = this.props;
    const selectedItem = this.getSelectedItem();

    if (!selectedItem) {
      return;
    }

    this.props.resetPortfolio();
    this.props.unselectComparisonItem(selectedItem);

    if (comparison.portfolioRiskProfile !== undefined) {
      this.props.removePortfolioRiskProfile();
    }

    if (selectedItem && selectedItem._id.startsWith('new-')) {
      this.props.removeItem(selectedItem._id);
    }
  };

  savePortfolio = (portfolio: Portfolio) => {
    const { editedComparisonItem } = this.props;
    return this.props.onSavePortfolio(portfolio, undefined, false).then(() => {
      // Trigger a portfolio calculation after a successful save to force a refresh
      // of the selected comparison item so that the symbols are updated
      this.props.calculatePortfolio(editedComparisonItem);
    });
  };

  showComparisonRiskProfileModal = () => {
    this.showRiskProfileModal(RISK_PROFILE_MODAL_TYPE.COMPARISON);
  };

  showPortfolioRiskProfileModal = () => {
    this.showRiskProfileModal(RISK_PROFILE_MODAL_TYPE.PORTFOLIO);
  };

  getViewContent = () => {
    const { selectedComparisonItems, editedPortfolio, riskProfile, dark } = this.props;
    const selectedItem = selectedComparisonItems[0];

    // Display asset details only if risk profile includes a rule targetting the asset allocation.
    const profile = riskProfile || (editedPortfolio.riskProfile && editedPortfolio.riskProfile.profile);
    const showAssetDetails = profile && (profile.rules as RiskProfileRule[]).some(rule => rule.ruleType === RiskProfileRuleType.AssetAllocation);
    const splitCashAndFixedIncome = showAssetDetails && shouldSplitCashAndFixedIncome(profile);

    if (!selectedItem || !selectedItem._id) {
      return (
        <div className="risk-profile-no-item d-flex align-items-center justify-content-center">
          <FormattedMessage id="RiskProfile.SelectItemHelper" />
        </div>
      );
    }

    if (selectedItem.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      const canEdit = selectedItem.availableActions == null || selectedItem.availableActions.edit;

      const readOnly = !!selectedItem.providedBy || !!selectedItem.enterprise || selectedItem.overrideHoldings || !canEdit;

      if (selectedItem._id !== editedPortfolio._id) {
        return (
          <div className="loading-edit-portfolio d-flex align-items-center justify-content-center">
            <LoadingSpinner />
          </div>
        );
      }

      return (
        <EditPortfolio
          className="flex-fill"
          dark={dark}
          allowComplianceComparisons={false}
          disableSaveOptions
          showAssetDetails={showAssetDetails}
          splitCashAndFixedIncome={splitCashAndFixedIncome}
          showDollars={false}
          showMER={false}
          withCharts={false}
          onCancel={this.onCancelCreatePortfolio}
          onSave={this.savePortfolio}
          isReadOnly={readOnly}
          saveLabel={portfolioIsNew(editedPortfolio) ? formatMessage('Comparison.AddToComparison') : undefined}
          saveNotification={portfolioIsNew(editedPortfolio) ? formatMessage('HoldingsEditor.Created.PORTFOLIO') : undefined}
          displayRiskProfile={this.showPortfolioRiskProfileModal}
          onDeleteRiskProfile={this.onDeletePortfolioRiskProfile}
          canDeselect
          isRiskProfileView
        />
      );
    }

    return (
      <SingleItemView
        showAssetDetails={showAssetDetails}
        splitCashAndFixedIncome={splitCashAndFixedIncome}
        item={selectedItem}
        onDeselect={this.onCancelCreatePortfolio}
      />
    );
  };

  onCreateRiskProfile = () => {
    const { riskProfileModalType } = this.state;
    const { newRiskProfile, recommendedRiskProfile, user } = this.props;

    if (newRiskProfile) {
      if (newRiskProfile.profile && recommendedRiskProfile && userHasPermissionEnabled(user, PERMISSIONS.KYC_UP_RISK_WARNING)) {
        addUpRiskToProfile(newRiskProfile.profile, recommendedRiskProfile, RISK_SCORE_TYPE.ORDER);
      }

      switch (riskProfileModalType) {
        case RISK_PROFILE_MODAL_TYPE.COMPARISON: {
          this.attachProfileToComparison(newRiskProfile);
          break;
        }
        case RISK_PROFILE_MODAL_TYPE.PORTFOLIO: {
          this.attachProfileToPortfolio(newRiskProfile);
          break;
        }
      }
      return this.hideRiskProfileModal();
    }

    this.hideRiskProfileModal();
  };

  onSelectExistingProfile = (profile: ContactRiskProfile | FormattedFullOmniRiskProfile) => {
    this.attachProfileToComparison(profile);
    this.hideSelectRiskProfileModal();
  };

  onSelectComparisonRiskProfile = () => {
    const selectedItem = this.getSelectedItem();

    if (!selectedItem) {
      return;
    }

    this.props.resetPortfolio();
    this.props.unselectComparisonItem(selectedItem);
    this.props.removePortfolioRiskProfile();
  };

  onAddPortfolioRiskProfileToComparison = () => {
    const { comparison, questionnaires } = this.props;

    if (comparison.portfolioRiskProfile === undefined) {
      return;
    }
    const matchingRiskProfile = getOriginalRiskProfile(comparison.portfolioRiskProfile, questionnaires);
    if (matchingRiskProfile) {
      this.props.attachRiskProfile(matchingRiskProfile);
      this.props.removePortfolioRiskProfile();
      notification.success(formatMessage('RiskProfile.RiskProfileAdded.Body'), formatMessage('RiskProfile.RiskProfileAdded.Title'));
    }
  };

  onRemoveRiskProfile = () => {
    const { editedPortfolio } = this.props;

    this.props.removeRiskProfile();

    if (editedPortfolio.riskProfile != null) {
      this.props.attachPortfolioRiskProfile(editedPortfolio.riskProfile.profile);
    }
  };

  onChangeRiskProfile = () => {
    this.showRiskProfileModal(RISK_PROFILE_MODAL_TYPE.COMPARISON);
  };

  getRiskProfileModalSaveLabel = () => {
    const { riskProfileModalType } = this.state;

    return riskProfileModalType === RISK_PROFILE_MODAL_TYPE.COMPARISON ? formatMessage('RiskProfile.AddToComparison') : undefined;
  };

  getRiskProfileModalTitle = () => {
    const { riskProfileModalType } = this.state;

    return riskProfileModalType === RISK_PROFILE_MODAL_TYPE.PORTFOLIO ? formatMessage('CreateRiskProfile.RiskProfile') : undefined;
  };

  render() {
    const { locale, riskProfile, currentQuestionnaire, currentContact, user, readOnly, comparison, newRiskProfile, theme } = this.props;
    const { showRiskProfileModal, showSelectRiskProfileModal } = this.state;

    const comparedItem = this.getSelectedItem();

    const currentRiskProfile = riskProfile || comparison.portfolioRiskProfile;

    if (!user) {
      return null;
    }

    return (
      <>
        <div className="risk-profile-view">
          {currentRiskProfile != null && (
            <RiskProfileCompare
              onPortfolio={!!comparison.portfolioRiskProfile}
              profile={currentRiskProfile}
              comparedItem={comparedItem}
              readOnly={!!readOnly}
              onChangeProfile={this.onChangeRiskProfile}
              onRemoveProfile={this.onRemoveRiskProfile}
              onAddToComparison={this.onAddPortfolioRiskProfileToComparison}
              onSelectComparisonRiskProfile={this.onSelectComparisonRiskProfile}
              theme={theme}
              user={user}
            />
          )}

          {currentRiskProfile == null && (
            <div className={classNames('risk-profile-actions', { 'd-block': !userHasPermission(user, PERMISSIONS.CONTACTS_RISK_PROFILES_TAB) })}>
              <Button className="risk-profile-btn new-risk-profile" onClick={this.showComparisonRiskProfileModal}>
                <i className="icon-plus-med" />
                <FormattedMessage id="RiskProfile.CreateNewRiskProfile" />
              </Button>

              {userHasPermission(user, PERMISSIONS.CONTACTS_RISK_PROFILES_TAB) && (
                <Button className="risk-profile-btn select-risk-profile" onClick={this.showSelectRiskProfileModal}>
                  <i className="icon-people" />
                  <FormattedMessage id="RiskProfile.SelectFromContact" />
                </Button>
              )}
            </div>
          )}

          <div className="view-content flex-fill">{this.getViewContent()}</div>
        </div>

        {showRiskProfileModal && currentQuestionnaire !== undefined && newRiskProfile && (
          <CreateKycQuestionnaire
            isModal
            show={showRiskProfileModal}
            onSave={this.onCreateRiskProfile}
            onClose={this.hideRiskProfileModal}
            saveLabel={this.getRiskProfileModalSaveLabel()}
            title={this.getRiskProfileModalTitle()}
            riskProfiles={currentQuestionnaire.riskProfiles}
            newRiskProfile={newRiskProfile}
            minRiskCalculation={currentQuestionnaire.options.minRiskCalculation}
            scoreCalculation={currentQuestionnaire.options.scoreCalculation}
            isSaving={this.props.isSavingRiskProfile}
          />
        )}

        {showSelectRiskProfileModal && (
          <SelectContactRiskProfileModal
            user={user}
            show={showSelectRiskProfileModal}
            currentContact={currentContact != null ? currentContact.contact : undefined}
            onCancel={this.hideSelectRiskProfileModal}
            locale={locale}
            onSelect={this.onSelectExistingProfile}
            isSaving={this.props.isSavingRiskProfile}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  locale: state.intl.locale,
  questionnaires: state.kycQuestionnaire.questionnaires,
  currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
  newRiskProfile: state.kycQuestionnaire.newRiskProfile,
  recommendedRiskProfile: state.kycQuestionnaire.recommendedRiskProfile,
  currentContact: state.contacts.current,
  selectedComparisonItems: state.comparison.selectedComparisonItems,
  editedPortfolio: state.editPortfolio.portfolio,
  editedComparisonItem: state.editPortfolio.comparisonItem,
  isSavingRiskProfile: selectIsLoadingSome(state, [
    SET_IS_ATTACHING_RISK_PROFILE.type,
    SET_IS_ATTACHING_PORTFOLIO_RISK_PROFILE.type,
    SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO.type,
    SET_IS_RESETTING_RISK_PROFILE.type,
  ]),
  theme: state.auth.user?.preferences?.theme ?? ThemeMode.DEFAULT,
});

export default connect(mapStateToProps, {
  getKycQuestionnaires,
  loadKycQuestionnaire,
  attachOmniRiskProfile,
  attachRiskProfile,
  removeRiskProfile,
  selectComparisonItem,
  unselectComparisonItem,
  loadPortfolioForEdit,
  getPortfolioById,
  resetPortfolio,
  removeItem,
  resetRiskProfile,
  attachPortfolioRiskProfile,
  removePortfolioRiskProfile,
  attachProfileToPortfolio,
  calculatePortfolio,
  setRecommendedRiskProfile,
})(RiskProfileView);
